import './Dashboard.css';

import React, {
  useEffect,
  useState,
} from 'react';

import {
  get,
  post,
  put,
} from 'aws-amplify/api';
import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signOut,
} from 'aws-amplify/auth';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';

// Configuration
import awsConfig from './aws-config';
import FormsSection from './FormsSection';
import MessagesSection from './MessagesSection';
// Components
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import TrackingSection from './TrackingSection';
import UtilityBillsSection from './UtilityBillsSection';

// Do not reconfigure Amplify here, it should be configured in index.js only

const Dashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeSection, setActiveSection] = useState('forms');
  const [formProgress, setFormProgress] = useState({
    contract: false,
    nda: false,
    loa: false
  });
  const [billsUploaded, setBillsUploaded] = useState(false);
  const [analysisSubmitted, setAnalysisSubmitted] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState('not_started');
  const [apiError, setApiError] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
    
    // Set up an interval to refresh auth token
    const tokenRefreshInterval = setInterval(async () => {
      if (isAuthenticated) {
        try {
          // Refresh the authentication session
          await fetchAuthSession({ forceRefresh: true });
          console.log('Auth session refreshed');
        } catch (error) {
          console.error('Failed to refresh auth session:', error);
          // If refresh fails, redirect to login
          setIsAuthenticated(false);
          navigate('/login');
        }
      }
    }, 45 * 60 * 1000); // Refresh every 45 minutes
    
    return () => clearInterval(tokenRefreshInterval);
  }, [isAuthenticated, navigate]);

  const checkAuthStatus = async () => {
    try {
      const userInfo = await getCurrentUser();
      
      // Get user attributes for display info
      let attributes = {};
      try {
        attributes = await fetchUserAttributes();
      } catch (err) {
        console.warn('Error fetching user attributes:', err);
      }
      
      // Create user object with proper display information
      const userObj = {
        username: userInfo.username,
        email: attributes.email || '',
        displayName: attributes.name || attributes.email || userInfo.username,
        firstName: attributes.given_name || '',
        lastName: attributes.family_name || '',
      };
      
      setUser(userObj);
      setIsAuthenticated(true);
      
      // Fetch user progress from backend
      fetchUserProgress(userInfo.username);
    } catch (error) {
      console.log('User is not authenticated', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const getAuthToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens.idToken.toString();
    } catch (error) {
      console.error('Error getting auth token:', error);
      throw error;
    }
  };

  const fetchUserProgress = async (userId) => {
    try {
      // Use Amplify API directly
      console.log('Fetching progress for user:', userId);
      
      try {
        // Get auth token before making the request
        const token = await getAuthToken();
        
        const response = await get({
          apiName: 'UtilityAuditProAPI',
          path: `/users/${userId}/progress`,
          options: {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        }).response;
        
        if (response.statusCode === 200) {
          const data = await response.body.json();
          console.log('Progress data:', data);
          
          setFormProgress(data.formProgress || { contract: false, nda: false, loa: false });
          setBillsUploaded(data.billsUploaded || false);
          setAnalysisSubmitted(data.analysisSubmitted || false);
          setAnalysisStatus(data.analysisStatus || 'not_started');
          
          // Determine which section should be active
          if (!data.formProgress?.contract || !data.formProgress?.nda || !data.formProgress?.loa) {
            setActiveSection('forms');
          } else if (!data.billsUploaded) {
            setActiveSection('utility-bills');
          } else if (data.analysisSubmitted) {
            setActiveSection('tracking');
          }
          
          setApiError(null);
        } else {
          throw new Error(`API returned status code ${response.statusCode}`);
        }
      } catch (apiError) {
        console.error('API error fetching progress:', apiError);
        
        // Check if it's an unauthenticated error
        if (apiError.message?.includes('401') || apiError.message?.includes('403')) {
          // Try to refresh the session
          try {
            await fetchAuthSession({ forceRefresh: true });
            // If successful, we'll retry on the next render cycle
          } catch (authError) {
            console.error('Failed to refresh auth session:', authError);
            setIsAuthenticated(false);
            navigate('/login');
          }
        }
        
        setApiError(`Failed to fetch user progress: ${apiError.message}`);
        
        // Use default values
        setFormProgress({ contract: false, nda: false, loa: false });
        setBillsUploaded(false);
        setAnalysisSubmitted(false);
        setAnalysisStatus('not_started');
      }
    } catch (error) {
      console.error('Error in fetchUserProgress:', error);
      setApiError(`Error fetching user progress: ${error.message}`);
      
      // Use default values
      setFormProgress({ contract: false, nda: false, loa: false });
      setBillsUploaded(false);
      setAnalysisSubmitted(false);
      setAnalysisStatus('not_started');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      setIsAuthenticated(false);
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  const updateFormProgress = async (formName, completed) => {
    // Update local state
    setFormProgress(prev => ({
      ...prev,
      [formName]: completed
    }));
    
    // Update in backend
    if (user) {
      try {
        // Get auth token before making the request
        const token = await getAuthToken();
        
        await put({
          apiName: 'UtilityAuditProAPI',
          path: `/users/${user.username}/forms/${formName}`,
          options: {
            headers: {
              Authorization: `Bearer ${token}`
            },
            body: { completed }
          }
        });
        
        console.log(`${formName} progress updated successfully`);
      } catch (error) {
        console.error(`Error updating ${formName} progress:`, error);
        setApiError(`Failed to update form progress: ${error.message}`);
      }
    }
  };

  const handleBillUpload = (uploaded) => {
    setBillsUploaded(uploaded);
  };

  const submitForAnalysis = async () => {
    if (user) {
      try {
        // Get auth token before making the request
        const token = await getAuthToken();
        
        const response = await post({
          apiName: 'UtilityAuditProAPI',
          path: `/analysis/submit`,
          options: {
            headers: {
              Authorization: `Bearer ${token}`
            },
            body: { userId: user.username }
          }
        }).response;
        
        if (response.statusCode === 200) {
          setAnalysisSubmitted(true);
          setAnalysisStatus('in_progress');
          setActiveSection('tracking');
          setApiError(null);
        } else {
          throw new Error(`API returned status code ${response.statusCode}`);
        }
      } catch (error) {
        console.error('Error submitting for analysis:', error);
        setApiError(`Failed to submit for analysis: ${error.message}`);
      }
    } else {
      setApiError('User information not available. Please log in again.');
    }
  };

  const canSubmitForAnalysis = formProgress.contract && formProgress.nda && formProgress.loa && billsUploaded;

  if (isLoading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="dashboard-container">
      <Navbar user={user} onSignOut={handleSignOut} />
      <div className="dashboard-content">
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection}
          formProgress={formProgress}
          billsUploaded={billsUploaded}
          analysisSubmitted={analysisSubmitted}
        />
        <main className="main-content">
          {apiError && (
            <div className="api-error-banner">
              <p>{apiError}</p>
              <button onClick={() => setApiError(null)}>Dismiss</button>
            </div>
          )}
          
          {/* Debug info - remove in production */}
          {process.env.NODE_ENV !== 'production' && (
            <div className="debug-info">
              <h4>Debug Information:</h4>
              <p>User: {user?.displayName} ({user?.email})</p>
              <p>Active Section: {activeSection}</p>
              <p>Form Progress: Contract: {formProgress.contract ? 'Yes' : 'No'}, 
                 NDA: {formProgress.nda ? 'Yes' : 'No'}, 
                 LOA: {formProgress.loa ? 'Yes' : 'No'}</p>
              <p>Bills Uploaded: {billsUploaded ? 'Yes' : 'No'}</p>
              <p>Analysis Submitted: {analysisSubmitted ? 'Yes' : 'No'}</p>
              <p>Analysis Status: {analysisStatus}</p>
              <p>API Name: {awsConfig.API.endpoints[0].name}</p>
              <p>API Endpoint: {awsConfig.API.endpoints[0].endpoint}</p>
            </div>
          )}
          
          <Routes>
            <Route path="/" element={<FormsSection 
              formProgress={formProgress}
              updateFormProgress={updateFormProgress}
              user={user}
            />} />
            <Route path="forms" element={<FormsSection 
              formProgress={formProgress}
              updateFormProgress={updateFormProgress}
              user={user}
            />} />
            <Route path="utility-bills" element={<UtilityBillsSection 
              formsCompleted={formProgress.contract && formProgress.nda && formProgress.loa}
              onBillUpload={handleBillUpload}
              billsUploaded={billsUploaded}
              onSubmitForAnalysis={submitForAnalysis}
              canSubmitForAnalysis={canSubmitForAnalysis}
              user={user}
            />} />
            <Route path="tracking" element={<TrackingSection 
              analysisStatus={analysisStatus}
              setAnalysisStatus={setAnalysisStatus}
              user={user}
            />} />
            <Route path="messages" element={<MessagesSection user={user} />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;